/* ============================================================================
   ORW Product List -- Mobile infinite scroll
   ============================================================================
   On mobile viewports (<= 749px), replace the pagination UI with auto-loading
   of the next page when the user scrolls near the bottom of the grid.

   - Reads the next-page URL from the existing <a class="...page-btn--next">.
     CSS hides the pagination block on mobile but the markup remains in DOM
     so we can grab the URL from it.
   - Uses IntersectionObserver on a sentinel placed at the end of the grid.
   - Fetches the next page HTML, parses the product rows, appends them to the
     grid, updates the next URL from the response. Loops until no more pages.
   - On viewport resize across the 749px threshold, reload the page so the
     user gets the proper pagination back on desktop / tablet.
   ============================================================================ */
(function () {
  'use strict';

  var MOBILE_MAX = 749;
  function isMobile() {
    return window.innerWidth <= MOBILE_MAX;
  }

  /* Find pieces we need. Exit early if not on a collection page. */
  var grid = document.querySelector('[data-orw-product-grid]');
  if (!grid) return;

  /* The very first time we look, grab the next-page URL from the rendered
     pagination block. After the first load this is replaced from the AJAX
     response's pagination, NOT from the existing DOM (which got swapped). */
  function getInitialNextUrl() {
    var nextLink = document.querySelector('.orw-product-list__page-btn--next');
    return nextLink ? nextLink.getAttribute('href') : null;
  }

  var nextUrl = getInitialNextUrl();
  var loading = false;
  var done = !nextUrl;

  /* Sentinel element appended to the grid -- when it scrolls into view we
     trigger the next fetch. Sits outside row containers so it does not
     interfere with the 2-col grid layout inside rows. */
  /* `display:block` is required because Dawn base.css collapses `div:empty`
     to `display:none`, which would zero out the sentinel and silently kill
     the IntersectionObserver. */
  var sentinel = document.createElement('div');
  sentinel.className = 'orw-product-list__infinite-sentinel';
  sentinel.style.cssText = 'display:block;height:1px;width:100%;';
  grid.appendChild(sentinel);

  /* Tiny loader shown while a page is in flight. */
  var loader = document.createElement('div');
  loader.className = 'orw-product-list__infinite-loader';
  loader.style.cssText = 'display:none;text-align:center;padding:16px 0;font-family:"Helvetica Neue",Helvetica,Arial,sans-serif;font-size:14px;color:#7C7C7C;';
  loader.textContent = 'Loading more…';
  grid.appendChild(loader);

  function showLoader() { loader.style.display = 'block'; }
  function hideLoader() { loader.style.display = 'none'; }

  function loadNext() {
    if (loading || done || !nextUrl) return;
    loading = true;
    showLoader();

    fetch(nextUrl, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : Promise.reject(r); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newGrid = doc.querySelector('[data-orw-product-grid]');
        if (!newGrid) {
          done = true;
          return;
        }

        /* Append every new row (not the pagination, not the empty state). */
        var rows = newGrid.querySelectorAll('.orw-product-list__row');
        rows.forEach(function (row) {
          grid.insertBefore(row, sentinel);
        });

        /* Update next URL from the response's pagination. */
        var newNext = doc.querySelector('.orw-product-list__page-btn--next');
        nextUrl = newNext ? newNext.getAttribute('href') : null;
        if (!nextUrl) done = true;
      })
      .catch(function () { /* swallow; user can scroll-retry */ })
      .finally(function () {
        loading = false;
        hideLoader();
        if (done) {
          /* Remove sentinel + loader so observer disconnects. */
          if (sentinel.parentNode) sentinel.parentNode.removeChild(sentinel);
          if (loader.parentNode) loader.parentNode.removeChild(loader);
        }
      });
  }

  /* IntersectionObserver fires when the sentinel becomes visible. rootMargin
     gives some lead time so the user does not have to hit the bottom edge. */
  var observer;
  function startObserving() {
    if (!isMobile()) return;
    if (observer) return;
    observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) loadNext();
      });
    }, { rootMargin: '400px 0px' });
    observer.observe(sentinel);
  }

  function stopObserving() {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
  }

  if (isMobile()) startObserving();

  /* If the user resizes the window across the breakpoint (devtools, tablet
     rotate, etc.), reload so pagination + grid layout return to the proper
     viewport defaults. Throttle so we do not reload on every resize tick. */
  var lastIsMobile = isMobile();
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      var nowMobile = isMobile();
      if (nowMobile !== lastIsMobile) {
        window.location.reload();
      }
    }, 250);
  }, { passive: true });
})();
